import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setShippingComplete, removeUnshippableItems, setActiveDeliveryMethod, setShippingAddress, clearUnshippableItems } from '../../redux/slices/checkoutSlice';
import { selectAllCartItems } from '../../redux/slices/cartSlice';
import { ErrorMessage } from '../common';
import ShippingMethodSection from './delivery/ShippingMethodSection';
import PickupMethodSection from './delivery/PickupMethodSection';
import SameDayMethodSection from './delivery/SameDayMethodSection';
import AddressSelection from '../address/AddressDropdown';
import { useGetAddressesQuery } from '../../redux/apis';
import LoadingSpinner from '../ui/LoadingSpinner';

// Helper function to check if an item is unshippable
const isItemUnshippable = (item, unshippableItems) => {
  const itemId = item.id || item.cartItemIndex;
  const method = item.deliveryMethod || 'shipping';
  
  // Check in the new structure
  if (typeof unshippableItems === 'object' && !Array.isArray(unshippableItems)) {
    // If unshippableItems is the new object structure
    if (unshippableItems[method]) {
      // Check all address groups for this method
      for (const addressId in unshippableItems[method]) {
        if (Array.isArray(unshippableItems[method][addressId]) && 
            unshippableItems[method][addressId].includes(itemId)) {
          return true;
        }
      }
    }
    
    // Also check legacy array for backward compatibility
    if (Array.isArray(unshippableItems.legacy) && unshippableItems.legacy.includes(itemId)) {
      return true;
    }
    
    return false;
  }
  
  // Fallback to legacy behavior if unshippableItems is still an array
  return Array.isArray(unshippableItems) && unshippableItems.includes(itemId);
};

// Helper to get FROM address ID for a shipping item
const getFromAddressId = (item) => {
  const fromAddress = item.shippingAddress || 
                     item.product?.shippingAddress || 
                     item.merchant?.shippingAddress;
  
  if (!fromAddress) return 'unknown';
  
  if (typeof fromAddress === 'number') {
    return fromAddress.toString();
  } else if (fromAddress.id) {
    return fromAddress.id.toString();
  } else if (fromAddress._id) {
    return fromAddress._id.toString();
  } else if (typeof fromAddress === 'string') {
    return fromAddress;
  }
  
  return 'unknown';
};

// Helper to get pickup address ID for a pickup item
const getPickupAddressId = (item) => {
  let pickupAddress = null;
  
  // Check item's pickupAddress first
  if (item.pickupAddress) {
    pickupAddress = item.pickupAddress;
  } 
  // Check product's pickupAddress next
  else if (item.product && item.product.pickupAddress) {
    pickupAddress = item.product.pickupAddress;
  }
  // Check merchant's address as fallback
  else if (item.merchant && item.merchant.address) {
    pickupAddress = item.merchant.address;
  }
  
  if (!pickupAddress) return 'unknown';
  
  // Extract ID from the address object or use it directly if it's a primitive
  if (typeof pickupAddress === 'number') {
    return pickupAddress.toString();
  } else if (typeof pickupAddress === 'object') {
    if (pickupAddress.id) {
      return pickupAddress.id.toString();
    } else if (pickupAddress._id) {
      return pickupAddress._id.toString();
    } else if (pickupAddress.city && pickupAddress.postalCode) {
      // Fallback to city-postalCode if no ID is available
      return `${pickupAddress.city}-${pickupAddress.postalCode}`;
    }
  } else if (typeof pickupAddress === 'string') {
    return pickupAddress;
  }
  
  return 'unknown';
};

const ShippingSection = () => {
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [groupCompletionStatus, setGroupCompletionStatus] = useState({
    shipping: {},
    pickup: {},
    sameDay: false
  });
  
  // Get user addresses (customer shipping addresses - TO addresses)
  const { 
    data: addresses = [], 
    isLoading: addressesLoading,
  } = useGetAddressesQuery(undefined, {
    skip: !useSelector(state => state.auth?.user)
  });
  
  // Get Redux state
  const { unshippableItems = { legacy: [] }, deliveryGroups, checkoutCurrency } = useSelector(state => state.checkout);
  
  // Get all cart items from the store using the new selector
  const allCartItemsFromStore = useSelector(selectAllCartItems);
  
  // Filter cart items by the checkout currency
  const cartItems = useMemo(() => {
    const currencyUpperCase = (checkoutCurrency || 'USD').toUpperCase();
    
    // Filter from allCartItemsFromStore
    const filteredItems = allCartItemsFromStore.filter(item => {
      const itemCurrency = (item.currency || 'USD').toUpperCase();
      return itemCurrency === currencyUpperCase;
    });
    
    console.log(`ShippingSection: Filtered ${filteredItems.length} items with currency ${currencyUpperCase} from ${allCartItemsFromStore.length} total items`);
    
    return filteredItems;
  }, [allCartItemsFromStore, checkoutCurrency]);
  
  // Load initial address from Redux if available
  useEffect(() => {
    const addressInRedux = deliveryGroups?.shipping?.address;
    if (addressInRedux && !selectedAddress) {
      setSelectedAddress(addressInRedux);
    }
  }, [deliveryGroups, selectedAddress]);
  
  // --> ADDED: Automatically select the default address when addresses load <--
  useEffect(() => {
    // Only run if addresses are loaded, not loading, and we haven't already selected an address
    if (!addressesLoading && addresses && addresses.length > 0 && !selectedAddress) {
      const defaultAddress = addresses.find(addr => addr.isDefault);
      if (defaultAddress) {
        console.log('Setting default shipping address:', defaultAddress.id);
        setSelectedAddress(defaultAddress);
        // Also update Redux state with the default address
        dispatch(setShippingAddress(defaultAddress)); 
      }
    }
  // Depend on addresses data and loading state
  }, [addresses, addressesLoading, selectedAddress, dispatch]); 
  
  // Group items by delivery method
  const { shippingItems, pickupItems, sameDayItems } = useMemo(() => {
    return cartItems.reduce((groups, item) => {
      // Check if item is unshippable using the Redux state
      const itemIsUnshippable = isItemUnshippable(item, unshippableItems);
    
      // Default to "shipping" if deliveryMethod is not specified
      const deliveryMethod = item.deliveryMethod || 'shipping';
      
      // Add to appropriate group with unshippable flag
      if (deliveryMethod === 'shipping') {
        groups.shippingItems.push({
          ...item,
          isUnshippable: itemIsUnshippable
        });
      } else if (deliveryMethod === 'pickup') {
        groups.pickupItems.push({
          ...item,
          isUnshippable: itemIsUnshippable
        });
      } else if (deliveryMethod === 'sameDay') {
        groups.sameDayItems.push({
          ...item,
          isUnshippable: itemIsUnshippable
        });
      }
      
      return groups;
    }, { shippingItems: [], pickupItems: [], sameDayItems: [] });
  }, [cartItems, unshippableItems]);
  
  // Group shipping items by FROM address
  const shippingItemsByFromAddress = useMemo(() => {
    const groups = {};
    
    shippingItems.forEach(item => {
      const fromAddressId = getFromAddressId(item);
      if (!groups[fromAddressId]) {
        groups[fromAddressId] = [];
      }
      groups[fromAddressId].push(item);
    });
    
    // Log the groups
    Object.entries(groups).forEach(([fromAddressId, items]) => {
      const itemNames = items.map(item => item.name || item.product?.name).join(', ');
    });
    
    return groups;
  }, [shippingItems]);
  
  // Group pickup items by pickup address
  const pickupItemsByLocation = useMemo(() => {
    const groups = {};
    
    pickupItems.forEach(item => {
      const pickupAddressId = getPickupAddressId(item);
      if (!groups[pickupAddressId]) {
        groups[pickupAddressId] = [];
      }
      groups[pickupAddressId].push(item);
    });
    
    // Log the pickup groups
    Object.entries(groups).forEach(([pickupAddressId, items]) => {
      const itemNames = items.map(item => item.name || item.product?.name).join(', ');
    });
    
    return groups;
  }, [pickupItems]);
  
  // Handle shipping address change
  const handleAddressChange = (e) => {
    if (!e || !e.target) return;
    
    const selectedAddressId = e.target.value;
    if (selectedAddressId === 'new') {
      // Handle new address case
      return;
    }
    
    // Find the selected address from our addresses array
    const newSelectedAddress = addresses.find(addr => addr.id === selectedAddressId);
    if (newSelectedAddress) {
      setSelectedAddress(newSelectedAddress);
      
      // Update Redux with the new address
      dispatch(setShippingAddress(newSelectedAddress));
      
      // Reset shipping completion status for recalculation
      setGroupCompletionStatus(prev => ({
        ...prev,
        shipping: {}
      }));
    }
  };
  
  // Handle completion status for shipping address groups
  const handleShippingCompletion = useCallback((isComplete, meta) => {
    const addressId = meta?.addressId || 'default';
    
    // Handle unshippable items
    if (meta && meta.unshippable && meta.items) {
      // Only dispatch to Redux, don't maintain duplicate local state
      dispatch(removeUnshippableItems({
        method: 'shipping',
        addressId,
        items: meta.items
      }));
      return;
    } else if (meta && !meta.unshippable && isComplete) {
      // If complete and no longer unshippable, clear previous unshippable items for this group
      dispatch(clearUnshippableItems({ method: 'shipping', addressId }));
    }
    
    // Update completion status for this specific address group
    setGroupCompletionStatus(prev => ({
      ...prev,
      shipping: {
        ...prev.shipping,
        [addressId]: isComplete
      }
    }));
  }, [dispatch]);
  
  const handlePickupCompletion = useCallback((isComplete, meta) => {
    // Handle unshippable items
    if (meta && meta.unshippable && meta.items) {
      // Only dispatch to Redux, don't maintain duplicate local state
      dispatch(removeUnshippableItems({
        method: 'pickup',
        addressId: meta.addressId || 'default',
        items: meta.items
      }));
      return;
    } else if (meta && !meta.unshippable && isComplete) {
      // If complete and no longer unshippable, clear previous unshippable items for this group
      dispatch(clearUnshippableItems({ method: 'pickup', addressId: meta.addressId || 'default' }));
    }
    
    const locationId = meta?.locationId || 'default';
    
    // Update completion status for this specific location group
    setGroupCompletionStatus(prev => ({
      ...prev,
      pickup: {
        ...prev.pickup,
        [locationId]: isComplete
      }
    }));
  }, [dispatch]);
  
  const handleSameDayCompletion = useCallback((isComplete, meta) => {
    // Handle unshippable items
    if (meta && meta.unshippable && meta.items) {
      // Only dispatch to Redux, don't maintain duplicate local state
      dispatch(removeUnshippableItems({
        method: 'sameDay',
        addressId: meta.addressId || 'default',
        items: meta.items
      }));
      return;
    } else if (meta && !meta.unshippable && isComplete) {
      // If complete and no longer unshippable, clear previous unshippable items for this group
      dispatch(clearUnshippableItems({ method: 'sameDay', addressId: meta.addressId || 'default' }));
    }
    
    setGroupCompletionStatus(prev => ({
      ...prev,
      sameDay: isComplete
    }));
  }, [dispatch]);
  
  // Show warning if any items are unshippable
  useEffect(() => {
    // Count unshippable items from Redux state
    let unshippableCount = 0;
    
    // Count from legacy array
    if (Array.isArray(unshippableItems.legacy)) {
      unshippableCount += unshippableItems.legacy.length;
    }
    
    // Count from method-specific objects
    ['shipping', 'pickup', 'sameDay'].forEach(method => {
      if (unshippableItems[method]) {
        Object.values(unshippableItems[method]).forEach(items => {
          if (Array.isArray(items)) {
            unshippableCount += items.length;
          }
        });
      }
    });
    
    if (unshippableCount > 0) {
      setError(`${unshippableCount} item(s) cannot be shipped to your address.`);
    } else {
      setError(null);
    }
  }, [unshippableItems]);
  
  // Check if all required sections are complete
  useEffect(() => {
    // Get active shipping from-address groups
    // const shippingFromAddressGroups = Object.keys(shippingItemsByFromAddress);

    // Filter for shipping groups that still have shippable items for the current selectedAddress (TO address)
    const relevantShippingFromAddressGroups = Object.keys(shippingItemsByFromAddress).filter(fromAddressId => {
      const itemsInGroup = shippingItemsByFromAddress[fromAddressId];
      return itemsInGroup.some(item => !isItemUnshippable(item, unshippableItems)); // isItemUnshippable uses global unshippableItems state
    });
    
    // Check if each RELEVANT shipping from-address group is complete
    const allShippingGroupsComplete = relevantShippingFromAddressGroups.length === 0 || 
      relevantShippingFromAddressGroups.every(addressId => 
        groupCompletionStatus.shipping[addressId]
      );
    
    // Get active pickup location groups
    const pickupLocationGroups = Object.keys(pickupItemsByLocation);
    
    // Check if each pickup location group is complete
    const allPickupGroupsComplete = pickupLocationGroups.length === 0 ||
      pickupLocationGroups.every(locationId =>
        groupCompletionStatus.pickup[locationId]
      );
    
    // Count how many unshippable items we have in the shipping section
    const unshippableShippingItems = shippingItems.filter(item => item.isUnshippable);
    const allShippingItemsUnshippable = 
      shippingItems.length > 0 && 
      unshippableShippingItems.length === shippingItems.length;
    
    // Get active method groups
    const activeMethodGroups = [];
    if (relevantShippingFromAddressGroups.length > 0) { // New logic: if there are relevant groups for shipping
      activeMethodGroups.push("shipping");
    }
    if (pickupItems.length > 0) activeMethodGroups.push("pickup");
    if (sameDayItems.length > 0) activeMethodGroups.push("sameDay");
    
    // Check if all active method groups are complete
    const allMethodsComplete = activeMethodGroups.every(method => {
      if (method === "shipping") {
        return allShippingGroupsComplete && selectedAddress;  // Also require a selected address
      } else if (method === "pickup") {
        return allPickupGroupsComplete;
      }
      return groupCompletionStatus[method];
    });
    
    // consider shipping as complete
    const hasAnyDeliverableItems = 
      (pickupItems.length > 0 && pickupItems.some(item => !isItemUnshippable(item, unshippableItems))) || 
      (shippingItems.length > 0 && shippingItems.some(item => !isItemUnshippable(item, unshippableItems)));
      
    dispatch(setShippingComplete(hasAnyDeliverableItems && allMethodsComplete));
    
    // Show warning about unshippable items
    let unshippableCount = 0;
    
    // Count from legacy array
    if (Array.isArray(unshippableItems.legacy)) {
      unshippableCount += unshippableItems.legacy.length;
    }
    
    // Count from method-specific objects
    ['shipping', 'pickup', 'sameDay'].forEach(method => {
      if (unshippableItems[method]) {
        Object.values(unshippableItems[method]).forEach(items => {
          if (Array.isArray(items)) {
            unshippableCount += items.length;
          }
        });
      }
    });
    
    if (unshippableCount > 0) {
      if (unshippableCount === cartItems.length) {
        setError(`None of your items can be shipped to your address. Please try a different address.`);
      } else {
        setError(`${unshippableCount} item(s) cannot be shipped to your address.`);
      }
    } else {
      setError(null);
    }
  }, [groupCompletionStatus, dispatch, shippingItems, pickupItems, sameDayItems, unshippableItems, cartItems.length, shippingItemsByFromAddress, pickupItemsByLocation, selectedAddress]);
  
  // Create an address map to convert address IDs to sequential numbers
  const createAddressMap = (addressIds) => {
    const addressMap = {};
    addressIds.forEach((id, index) => {
      addressMap[id] = index + 1; // Start from 1 for user-friendly numbering
    });
    return addressMap;
  };

  // Create maps for from addresses and pickup locations
  const fromAddressMap = useMemo(() => 
    createAddressMap(Object.keys(shippingItemsByFromAddress)), 
    [shippingItemsByFromAddress]
  );
  
  const pickupLocationMap = useMemo(() => 
    createAddressMap(Object.keys(pickupItemsByLocation)), 
    [pickupItemsByLocation]
  );

  // Check if we have multiple shipping addresses or pickup locations
  const hasMultipleShippingAddresses = Object.keys(shippingItemsByFromAddress).length > 1;
  const hasMultiplePickupLocations = Object.keys(pickupItemsByLocation).length > 1;
  
  return (
    <div className="mb-6">
      {/* <h2 className="text-xl font-semibold mb-4">Delivery Information</h2> */}
      
      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError(null)}
          className="mb-4"
        />
      )}
      
      <div className="space-y-6">
        {/* Single shipping address selection for all shipping items */}
        {shippingItems.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <h3 className="font-medium text-lg mb-3">Shipping Address</h3>
            {addressesLoading ? (
              <LoadingSpinner />
            ) : (
              <AddressSelection 
                value={selectedAddress?.id || ''}
                onChange={handleAddressChange}
                name="shippingAddress"
                label=""
                fluid={true}
                placeholder="Select a shipping address"
                showAddNew={true}
                required={true}
              />
            )}
          </div>
        )}
        
        {/* Regular Shipping Sections - One per FROM address */}
        {Object.entries(shippingItemsByFromAddress).map(([fromAddressId, itemsInGroup]) => (
          <ShippingMethodSection
            key={`shipping-group-${fromAddressId}`}
            addressId={fromAddressId}
            title={`Shipping from ${itemsInGroup[0]?.merchant?.username || 'merchant'}`}
            items={itemsInGroup}
            onComplete={(isSuccess, data) => handleShippingCompletion(isSuccess, data)}
            setError={setError}
            sharedAddress={selectedAddress}
          />
        ))}
        
        {/* Pickup Sections - One per pickup location */}
        {Object.entries(pickupItemsByLocation).map(([locationId, itemsInGroup]) => (
          <PickupMethodSection
            key={`pickup-group-${locationId}`}
            locationId={locationId}
            title={`Pickup from ${itemsInGroup[0]?.merchant?.username || 'merchant'}`}
            items={itemsInGroup}
            onComplete={(isSuccess, data) => handlePickupCompletion(isSuccess, data)}
            setError={setError}
          />
        ))}
        
        {/* Same-Day Delivery Section */}
        {sameDayItems.length > 0 && (
          <SameDayMethodSection
            title="Same-Day Delivery Items"
            items={sameDayItems}
            onComplete={handleSameDayCompletion}
            setError={setError}
          />
        )}
        
        {/* Show message if no items have delivery methods */}
        {shippingItems.length === 0 && pickupItems.length === 0 && sameDayItems.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
            <p className="text-yellow-700">
              No items with delivery methods found. Please add items to your cart.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingSection;